import { useState } from 'react';
import { findSpellById } from '@/data/spells';
import { SpellCard, type SpellCardTag } from '@/components/molecules';
import type { Character } from '@/schemas/character';
import type { Spell } from '@/schemas/spell';
import { SheetSection } from './SheetSection';

/**
 * **FI-79 bonus (2026-05-23)** — Sub-organism per la sezione "Incantesimi"
 * della scheda PG (`<FullSheet>`). Sostituisce la lista nuda di nomi
 * (`<ul>` con bullet `▸ Nome Spell`) con `<SpellCard>` read-only —
 * pattern parallelo a `<FullSheetMoves>` che usa `<MoveCard>`.
 *
 * Risultato: l'utente che apre la scheda Hub di un casteggiante può
 * leggere description di ogni incantesimo inline (clamp 3 righe +
 * chevron full) senza dover andare al manuale.
 *
 * **Read-only mode**: no `leading` (no checkbox), no `labelHtmlFor` →
 * SpellCard resta `<div>` non-cliccabile. Solo il chevron è interattivo
 * per espandere description full ↔ clamp.
 *
 * **Tags**: solo lvl (lvl-0 / lvl-1) + state-tags raw del catalogo
 * ('continuato'). Nessuna pillola auto/pick/picked perché in scheda PG
 * la distinzione non ha senso — sono tutte "le mie spell".
 */
export function FullSheetSpells({ character }: { character: Character }) {
  if (character.spells.length === 0) {
    return (
      <SheetSection title="Incantesimi">
        <p className="text-ink-soft italic">Nessun incantesimo selezionato.</p>
      </SheetSection>
    );
  }
  const grouped = groupSpellsByLevel(character);
  if (grouped.length === 0) {
    return (
      <SheetSection title="Incantesimi">
        <p className="text-ink-soft italic">Nessun incantesimo selezionato.</p>
      </SheetSection>
    );
  }
  return (
    <SheetSection title="Incantesimi">
      <div className="flex flex-col gap-3">
        {grouped.map(({ level, spells }) => (
          <LevelGroup key={level} level={level} spells={spells} />
        ))}
      </div>
    </SheetSection>
  );
}

function LevelGroup({ level, spells }: { level: number; spells: readonly Spell[] }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="font-display text-ink-soft text-[0.72rem] tracking-[0.08em] uppercase">
        {level === 0 ? 'Trucchetti (livello 0)' : `Livello ${level}`}
      </div>
      <div className="grid grid-cols-1 gap-[0.7rem] md:grid-cols-2">
        {spells.map((spell) => (
          <SheetSpellCard key={spell.id} spell={spell} />
        ))}
      </div>
    </div>
  );
}

function SheetSpellCard({ spell }: { spell: Spell }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <SpellCard
      spell={spell}
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
      tags={buildSheetSpellTags(spell)}
    />
  );
}

/**
 * Tags per la modalità scheda PG (read-only):
 *  - lvl-0 / lvl-1: pillola livello (uniformata al pattern SpellCard del builder)
 *  - state raw ('continuato', ecc.): se presenti nei `spell.tags`
 *
 * Non include pillole `auto` / `pick` / `picked` perché in scheda non ha
 * senso distinguere (sono tutte spell del PG).
 */
function buildSheetSpellTags(spell: Spell): SpellCardTag[] {
  const tags: SpellCardTag[] = [];
  tags.push({
    label: spell.level === 0 ? 'LV 0' : `LV ${spell.level}`,
    variant: spell.level === 0 ? 'lvl-0' : 'lvl-1',
  });
  if (spell.tags.includes('continuato')) {
    tags.push({ label: 'continuato', variant: 'state' });
  }
  return tags;
}

/**
 * Raggruppa le spell del PG per livello, risolvendo gli ID via
 * `findSpellById` (skip silenzioso per ID non in catalogo — defensive
 * contro PG corrotti). Ordine: livelli ascendenti (L0 prima, L9 dopo).
 */
function groupSpellsByLevel(
  character: Character,
): readonly { level: number; spells: readonly Spell[] }[] {
  const byLevel = new Map<number, Spell[]>();
  for (const spellRef of character.spells) {
    const spell = findSpellById(spellRef.id);
    if (!spell) continue;
    const list = byLevel.get(spell.level) ?? [];
    list.push(spell);
    byLevel.set(spell.level, list);
  }
  return [...byLevel.entries()]
    .sort(([a], [b]) => a - b)
    .map(([level, spells]) => ({ level, spells }));
}
